import { z } from 'zod';

/**
 * KI-Anbindung (#1336). Der API-Schlüssel eines Anbieters verlässt den Server
 * nie — die Oberfläche erfährt über `hasApiKey` nur, ob einer hinterlegt ist,
 * und schickt beim Speichern nur dann ein `ApiKey`-Feld, wenn er geändert
 * werden soll.
 */
export const kiProviderSchema = z.object({
  Provider: z.string(),
  Label: z.string(),
  /** Hinweistext für das Modellfeld, z. B. „z. B. claude-opus-5". */
  Hint: z.string().optional().default(''),
  /** Ist der Anbieter serverseitig schon angebunden? */
  isAvailable: z.boolean(),
  Model: z.string().optional().default(''),
  /** Abweichender Endpunkt (OpenAI-kompatible Server, Azure). */
  BaseUrl: z.string().optional().default(''),
  /** Nur watsonx.ai. */
  ProjectId: z.string().optional().default(''),
  /**
   * Eigener Agent für die Zuordnung im Posteingang. Leer heißt: derselbe wie
   * für den Chat. Nur bei IBM watsonx Orchestrate von Bedeutung.
   */
  ProjectIdPosteingang: z.string().optional().default(''),
  ProjectIdWebsuche: z.string().optional().default(''),
  WebsucheBezeichnung: z.string().optional().default(''),
  hasApiKey: z.boolean(),
  isActive: z.boolean(),
  /**
   * Ob der Anbieter die Werkzeuge von DiNo ausführen kann. IBM watsonx
   * Orchestrate nimmt die Werkzeugliste entgegen und ignoriert sie: es führt
   * seine eigenen Agenten aus, nicht unsere Abfragen.
   */
  supportsTools: z.boolean().default(false),
  /** Hat dieser Anbieter in den letzten dreißig Tagen in DiNo nachgesehen? */
  canLookUp: z.boolean().default(false),
  lastToolUse: z.string().nullable().optional(),
});

export const kiConfigSchema = z.object({
  /** Hauptschalter: aus heißt, jede KI-Funktion schweigt. */
  Enabled: z.boolean(),
  /**
   * Nur für das erste Testing: Benutzer, für die die KI läuft.
   * **Leer bedeutet alle.** Später übernimmt das die Erweiterung, und dann
   * gilt die Freischaltung für alle oder für niemanden.
   */
  EnabledUserArray: z.array(z.number()).default([]),
  ProviderArray: z.array(kiProviderSchema),
});

export type IKiProvider = z.infer<typeof kiProviderSchema>;
export type IKiConfig = z.infer<typeof kiConfigSchema>;

/** Was ein normaler Benutzer wissen darf — steuert das Chat-Fenster. */
export interface IKiStatus {
  Enabled: boolean;
  /** Eingeschaltet **und** ein brauchbarer Anbieter mit Schlüssel. */
  Ready: boolean;
  /** Ob das aktive Modell in DiNo nachsehen kann. */
  SupportsTools?: boolean;
  /**
   * Kann das Modell in DiNo nachsehen? **Nicht** dasselbe wie
   * `SupportsTools`: watsonx nimmt keine Werkzeuge von DiNo entgegen, sein
   * Agent bringt aber eigene mit, die über `/api/ki/tool` zurückrufen.
   * Belegt durch einen tatsächlichen Aufruf in den letzten dreißig Tagen —
   * nicht behauptet, damit die Auskunft nicht stehen bleibt, wenn drüben
   * jemand die Hüllen aushängt.
   */
  CanLookUp?: boolean;
  /**
   * Ob der eingestellte Anbieter eine Datei **inhaltlich** lesen kann. Wenn
   * nicht, wird dafür kein anderer angesprochen — die Wege, die davon leben,
   * bleiben verborgen (siehe `kiReadsDocuments`).
   */
  SupportsAttachments?: boolean;
  /**
   * Hält sich der Anbieter an DiNos Systemauftrag? Bei watsonx nicht — dort
   * gehen die eigenen Anweisungen des Agenten vor. Der Antwortentwurf hängt
   * daran, denn seine Regeln (keine Rechtsauskunft, nichts erfinden) stehen
   * im Auftrag.
   */
  HonorsSystemPrompt?: boolean;
  Provider: string | null;
  ProviderLabel?: string | null;
  Model: string | null;
}

/**
 * Kann die KI eine Datei auslesen — eingerichtet **und** mit einem Anbieter,
 * der Dateien annimmt? Eine Stelle für alle Einstiege, damit keiner einen
 * Knopf zeigt, der nur in eine Fehlermeldung führt.
 */
export const kiReadsDocuments = (status?: IKiStatus | null): boolean =>
  status?.Ready === true && status?.SupportsAttachments === true;

/**
 * Eine Datei, die an eine Chat-Frage gehängt wird (#1336).
 *
 * Sie wird **nicht** gespeichert und bekommt keine id: sie hängt an genau
 * dieser Anfrage. Damit kann ein Werkzeugaufruf keine beliebige Datei
 * anfordern — dieselbe Überlegung wie beim kurzlebigen Werkzeug-Token.
 */
export interface IKiAttachment {
  FileName: string;
  MimeType: string;
  /** Base64, ohne `data:`-Kopf (der Server schneidet ihn notfalls ab). */
  Data: string;
}

/** Was der Chat als Anhang annimmt — Serverseite prüft es noch einmal. */
export const KI_ATTACHMENT_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
];

/** 10 MB, wie serverseitig in `chat.MAX_ATTACHMENT_BYTES`. */
export const KI_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

export interface IKiUsage {
  inputTokens?: number | null;
  outputTokens?: number | null;
}

export interface IKiAnswer {
  Text: string;
  Provider: string;
  ProviderLabel: string;
  Model: string | null;
  DurationMs: number;
  Usage: IKiUsage;
  Iterations: number;
  /** Welche Werkzeuge das Modell in DiNo nachgeschlagen hat. */
  ToolCallArray?: IKiToolCall[];
  /** Was zum Anlegen vorbereitet wurde — noch nicht geschrieben. */
  ProposalArray?: IKiProposal[];
  /** Nur im Vergleich gesetzt: dieser Anbieter ist gescheitert. */
  Error?: string | null;
}

export interface IKiToolCall {
  name: string;
  input?: Record<string, unknown>;
}

/**
 * Was die KI zum Anlegen vorbereitet hat (#1336).
 *
 * Die Werkzeuge schreiben nichts. Sie liefern einen Vorschlag, den die
 * Oberfläche als Karte zeigt; erst der Klick des Benutzers ruft den regulären
 * Endpunkt aus **seiner** Sitzung auf. `Payload` hat deshalb genau die Form,
 * die dieser Endpunkt erwartet, und `Display` trägt die aufgelösten Klarnamen
 * — an einer id sieht niemand, dass der falsche Kollege gemeint ist.
 */
interface IKiProposalBase {
  Label: string;
  /** Was das Werkzeug beim Normalisieren angepasst hat, im Klartext. */
  Hinweise: string[];
}

export interface IKiTaskProposal extends IKiProposalBase {
  Kind: 'task';
  Payload: IKiTaskPayload;
  Display: {
    Zustaendige?: string[];
    Vorgang?: string | null;
    Mandant?: string | null;
  };
}

/** Ein bewerteter Mandanten-Treffer zu einem Eigentümer-Namen. */
export interface IKiOwnerMatch {
  idLegalClient: number;
  FirstName?: string | null;
  LastName?: string | null;
  Surname?: string | null;
  Birthdate?: string | null;
  score?: number;
}

/** Was mit einem ausgelesenen Eigentümer geschehen soll. */
export type IKiOwnerDecision = 'zusammenfuehren' | 'neu' | 'ignorieren';

/**
 * Ein Eigentümer aus Abteilung I, so wie er im Auszug stand — plus die
 * Mandanten, die DiNo dazu gefunden hat.
 */
export interface IKiGrundbuchOwner {
  lfdNr?: string | null;
  Name?: string | null;
  Vorname?: string | null;
  Nachname?: string | null;
  Geburtsname?: string | null;
  Geburtsdatum?: string | null;
  AnteilText?: string | null;
  Gemeinschaftsverhaeltnis?: string | null;
  GrundlageDerEintragung?: string | null;
  eingetragenAm?: string | null;
  istGeloescht?: boolean;
  /** Die fünf besten Treffer, absteigend bewertet. */
  MatchArray?: IKiOwnerMatch[];
  /** Vorauswahl des Servers — der Mensch entscheidet. */
  Vorschlag?: IKiOwnerDecision;
  idLegalClientVorschlag?: number | null;
}

/** Ergebnis von `POST /ki/grundbuch/owners/resolve`. */
export interface IKiOwnerResolution {
  /** Fertige Eigentümerzeilen für das Blatt-fullObject. */
  OwnerArray: Record<string, unknown>[];
  /** Wie viele Mandanten dabei neu angelegt wurden. */
  Angelegt: number;
  /** Was nicht übernommen werden konnte, im Klartext. */
  Uebersprungen: string[];
}

/**
 * Ein ausgelesener Grundbuchauszug (#1336).
 *
 * `Payload` hat die Form, die `GrundbuchblattEditDialog` als `initialDraft`
 * annimmt — ohne ids, die vergibt der Editor. `idGrundbuchblatt` ist gesetzt,
 * wenn es das Blatt in DiNo schon gibt: dann wird ergänzt statt angelegt.
 */
export interface IKiGrundbuchProposal extends IKiProposalBase {
  Kind: 'grundbuchblatt';
  Payload: Record<string, unknown>;
  idGrundbuchblatt: number | null;
  Display: {
    Amtsgericht?: string | null;
    Gemarkung?: string | null;
    EigentuemerArray?: IKiGrundbuchOwner[];
    AnzahlGrundstuecke?: number;
    AnzahlAbteilung2?: number;
    AnzahlAbteilung3?: number;
    Confidence?: 'hoch' | 'mittel' | 'niedrig' | null;
    Reason?: string | null;
  };
}

/**
 * Unterscheidbare Union über `Kind`. Kommt eine weitere Vorschlagsart dazu
 * (Vorgang anlegen, Registerauszug abrufen, Export), wird sie hier ergänzt —
 * `switch (proposal.Kind)` zwingt die Oberfläche dann, sie zu behandeln.
 */
/**
 * Eine fertige Datei zum Herunterladen — heute die Umwandlung PDF → Word.
 * Die Datei kommt aus DiNo und war nie beim Anbieter.
 */
export interface IKiDownloadProposal {
  Kind: 'download';
  Data: {
    FileName: string;
    MediaType: string;
    Base64: string;
    Pages?: number;
    Note?: string;
  };
}

/** Ein Treffer im Mandantenstamm zu einer Gesellschaft. */
export interface IKiCompanyMatch {
  idLegalClient: number;
  LastName?: string | null;
  RegisterNumber?: string | null;
  Email?: string | null;
  score: number;
  matchedFields?: string[];
}

/**
 * Ein Beteiligter, wie ihn die Urkunde nennt — plus der Abgleich mit dem
 * Mandantenstamm (#1336).
 *
 * Personen und Gesellschaften stecken bewusst im selben Typ: eine Urkunde
 * nennt beide nebeneinander, und die Karte zeigt sie in einer Liste. `art`
 * sagt, welche Felder gefüllt sind.
 */
export interface IKiBeteiligter {
  art: 'person' | 'gesellschaft';
  rolle?: string | null;
  Bezeichnung: string;
  // Person
  Vorname?: string | null;
  Nachname?: string | null;
  Geburtsname?: string | null;
  Geburtsdatum?: string | null;
  Geburtsort?: string | null;
  // Gesellschaft
  Firma?: string | null;
  Rechtsform?: string | null;
  Sitz?: string | null;
  Registergericht?: string | null;
  Registernummer?: string | null;
  Registerart?: string | null;
  Registerkennziffer?: string | null;
  idCourt?: number | null;
  idBusinessForm?: number | null;
  // Anschrift (wird nicht mit angelegt, nur angezeigt)
  Strasse?: string | null;
  Hausnummer?: string | null;
  Postleitzahl?: string | null;
  Ort?: string | null;
  // Abgleich
  MatchArray?: (IKiOwnerMatch | IKiCompanyMatch)[];
  Vorschlag?: IKiOwnerDecision;
  idLegalClientVorschlag?: number | null;
}

/** Die Beteiligten einer Urkunde als Mandanten-Vorschlag (#1336). */
export interface IKiMandantProposal extends IKiProposalBase {
  Kind: 'mandant';
  BeteiligteArray: IKiBeteiligter[];
  Display: {
    Dokumentart?: string | null;
    Confidence?: 'hoch' | 'mittel' | 'niedrig' | null;
    Reason?: string | null;
  };
}

/**
 * Ein Antwortentwurf auf eine eingegangene E-Mail (#1336).
 *
 * `OffenePunkte` ist der wichtige Teil: was vor dem Absenden noch zu
 * entscheiden oder einzusetzen ist. Ein Entwurf, der **fast** stimmt, ist
 * gefährlicher als einer, der offensichtlich nicht taugt.
 */
export interface IKiAntwortEntwurf {
  Subject: string | null;
  Body: string;
  OffenePunkte: string[];
  HatVorgangskontext: boolean;
  AnzahlTextbausteine: number;
  ProviderLabel?: string | null;
  Model?: string | null;
  DurationMs?: number | null;
}

/** Ein einzelner Ausweis auf dem Sammelfoto (#1336). */
export interface IKiAusweisKarte {
  Nummer: number;
  /** Der freigestellte Ausschnitt als Base64-PNG. `null`, wenn sich keiner
   *  bilden ließ — dann wird diese Karte nicht abgelegt. */
  Bild: string | null;
  Seite: 'vorderseite' | 'rueckseite' | 'unklar';
  Dokumentart: string;
  Nachname: string | null;
  Vorname: string | null;
  Geburtsdatum: string | null;
  Ausweisnummer: string | null;
  /** Stammen die Angaben aus der nachgerechneten MRZ? */
  AusMrz: boolean;
  /** Hat die Ausweisnummer die Prüfziffer bestanden? */
  NummerGeprueft: boolean;
  /** Über die Ausweisnummer von der Gegenseite übernommen, nicht selbst gelesen. */
  AusGegenseite?: boolean;
  MatchArray?: { idLegalClient: number; Name: string; score: number }[];
}

/**
 * Ausweise eines Sammelfotos, den Beteiligten zugeordnet (#1336).
 *
 * `OhneZuordnungArray` ist der wichtige Teil: was sich nicht sicher zuordnen
 * ließ, bleibt offen. Eine Ausweiskopie am falschen Mandanten fällt niemandem
 * mehr auf — sie sieht überall gleich richtig aus.
 */
export interface IKiAusweiseProposal extends IKiProposalBase {
  Kind: 'ausweise';
  idLegalTransaction: number;
  ZuordnungArray: {
    idLegalClient: number;
    Name: string;
    KartenArray: IKiAusweisKarte[];
  }[];
  OhneZuordnungArray: IKiAusweisKarte[];
  BeteiligteArray: { idLegalClient: number; Name: string }[];
  Display: { Reason?: string | null };
}

/** Was `POST /ki/ausweise/uebernehmen` zurückgibt. */
export interface IKiAusweiseResult {
  AngelegtArray: {
    idLegalClient: number;
    Name: string;
    idLegalClientDocumentVersion: number;
    Seiten: number;
  }[];
  Uebersprungen: string[];
}

/** Ein Anteilssatz, wie er in `LegalClientRelationshipShareholder` landet. */
export interface IKiGesellschaftsanteil {
  /** Bruchteil 0..1 — die Oberfläche zeigt `Shares*100 %`. */
  Shares: number | null;
  AmountOfShares: number | null;
  ValuePerShare: number | null;
  /** Summe der Nennbeträge — nur zur Anzeige. */
  Nennbetrag: number | null;
  isPartPayment: boolean;
  isShareByNonCashResources: boolean;
}

export interface IKiGesellschafter extends IKiBeteiligter {
  Anteilsnummern?: string | null;
  Anteil: IKiGesellschaftsanteil;
}

/** Was `POST /ki/gesellschafterliste/auslesen` zurückgibt. */
export interface IKiGesellschafterliste {
  /** `Fest`: die Gesellschaft ist bereits Mandant, nicht zu entscheiden. */
  Gesellschaft: IKiBeteiligter & { Fest: boolean };
  GesellschafterArray: IKiGesellschafter[];
  Stammkapital: number | null;
  Datum: string | null;
  Confidence: 'hoch' | 'mittel' | 'niedrig' | null;
  Reason: string | null;
  Hinweise: string[];
  ProviderLabel?: string | null;
  Model?: string | null;
  DurationMs?: number | null;
}

/** Was `POST /ki/gesellschafterliste/uebernehmen` zurückgibt. */
export interface IKiGesellschafterlisteResult {
  idGesellschaft: number;
  idRelationship: number;
  BeziehungNeu: boolean;
  AngelegtArray: { idLegalClient: number; Bezeichnung: string }[];
  VerknuepftArray: { idLegalClient: number; Bezeichnung: string }[];
  GesellschafterArray: {
    idLegalClient: number;
    Bezeichnung: string;
    /** hinzugefuegt · unveraendert · Anteil geaendert · Anteil ergaenzt */
    Status: string;
  }[];
  /** In DiNo Gesellschafter, aber nicht mehr auf der Liste — nur gemeldet. */
  NichtMehrAufDerListeArray: { idLegalClient: number; Bezeichnung: string }[];
  Uebersprungen: string[];
}

/** Ein Gesellschafter in der Kontrollkette — einheitlich für GL und AD. */
export interface IKiKontrollGesellschafter {
  art: 'person' | 'gesellschaft';
  Bezeichnung: string;
  Geburtsdatum?: string | null;
  Ort?: string | null;
  Firma?: string | null;
  Rechtsform?: string | null;
  Registergericht?: string | null;
  Registernummer?: string | null;
  /** Bruchteil 0..1; `null` z. B. bei einer KG (dort gibt es keine Prozente). */
  Anteil?: number | null;
  Nennbetrag?: number | null;
  /** Bei KG/OHG: „Kommanditist", „persönlich haftender Gesellschafter" … */
  Rolle?: string | null;
  Hafteinlage?: number | null;
}

export type IKiKontrollStatus =
  | 'ok'
  | 'nicht_gefunden'
  | 'mehrdeutig'
  | 'nicht_ermittelbar'
  | 'fehler';

/** Wer eine Gesellschaft leitet — Geschäftsführer, Vorstand, Präsident … */
export interface IKiKontrollLeiter {
  Bezeichnung: string;
  Funktion?: string | null;
  Geburtsdatum?: string | null;
  Ort?: string | null;
  /** Von Hand ergänzt, nicht aus dem Register. */
  manuell?: boolean;
}

/** Was `POST /ki/kontrollstruktur/schritt` für EINE Gesellschaft liefert. */
export interface IKiKontrollSchritt {
  Firma: string;
  Status: IKiKontrollStatus;
  Grund?: string | null;
  Register?: string | null;
  Registergericht?: string | null;
  /** „Gesellschafterliste vom …" oder „Aktueller Abdruck". */
  Quelle?: string | null;
  Hinweis?: string | null;
  Gesellschafter: IKiKontrollGesellschafter[];
  /** Geschäftsleitung laut Aktuellem Abdruck (AG, eG, e.V. … und auf Wunsch). */
  Geschaeftsleiter?: IKiKontrollLeiter[];
  /** Die Geschäftsleitung steht in keinem Register — von Hand ergänzen. */
  LeitungManuell?: boolean;
  Kandidaten?: {
    firma: string;
    gericht: string;
    art: string;
    nummer: string;
  }[];
}

/** Was `POST /ki/kontrollstruktur/speichern` zurückgibt. */
export type IKiKontrollSpeichernResult =
  | {
      Status: 'gespeichert';
      Vorgang: string;
      idLegalClient: number;
      Bezeichnung: string | null;
      Version: number | null;
      /** Hat der Vorgang einen GwG-Ordner? Sonst liegt es nur am Mandanten. */
      GwGOrdner: boolean;
    }
  | {
      Status: 'auswahl';
      Vorgang: string;
      Kandidaten: { idLegalClient: number; Bezeichnung: string | null }[];
    };

/** Karte: Kontrollstruktur prüfen (#1336). */
export interface IKiKontrollstrukturProposal extends IKiProposalBase {
  Kind: 'kontrollstruktur';
  Payload: {
    Firma: string;
    Registernummer: string | null;
    Registergericht: string | null;
    idLegalClient: number | null;
    idLegalTransaction: number | null;
  };
  Display: {
    Firma: string;
    Register: string | null;
    Vorgang: string | null;
    Aktenzeichen: string | null;
  };
}

/** Karte: die zuletzt erstellte Kontrollstruktur in einem Vorgang ablegen. */
export interface IKiKontrollstrukturAblageProposal extends IKiProposalBase {
  Kind: 'kontrollstruktur_ablage';
  Payload: { idLegalTransaction: number };
  Display: { Vorgang: string | null; Aktenzeichen: string | null };
}

/** Was `POST /ki/mandant/uebernehmen` zurückgibt. */
export interface IKiMandantResult {
  AngelegtArray: { idLegalClient: number; Bezeichnung: string }[];
  VerknuepftArray: { idLegalClient: number; Bezeichnung: string }[];
  Uebersprungen: string[];
}

/**
 * Ein vorbereiteter Abruf beim Handelsregister — Gesellschafterliste oder
 * Aktueller Abdruck.
 *
 * Abgerufen ist noch nichts. `Payload` ist genau der Body von
 * `POST /legalclient/{idLegalClient}/download-register-documents`; den
 * schickt erst der Klick, aus der Sitzung des Benutzers.
 *
 * Ist die Gesellschaft kein Mandant, ist `idLegalClient` null und
 * `Payload.OhneMandant` der Body von `POST /ki/registerabruf`: das Dokument
 * kommt dann nur zum Öffnen und Herunterladen, abgelegt wird nichts.
 */
export interface IKiRegisterAbrufProposal extends IKiProposalBase {
  Kind: 'registerabruf';
  idLegalClient: number | null;
  Payload: {
    types: ('AD' | 'SI' | 'GL')[];
    idLegalTransaction?: number;
    /** Eine bestimmte Gesellschafterliste (YYYY-MM-DD) statt der neuesten. */
    glDatum?: string;
    OhneMandant?: IKiRegisterabrufOhneMandant;
  };
  Display: {
    /** „Gesellschafterliste" oder „Aktueller Abdruck". */
    Dokument: string;
    Firma: string;
    /** z. B. „HRB 4711". */
    Register: string;
    Registergericht?: string | null;
    Vorgang?: string | null;
    /** Gezielt gewünschte Liste (YYYY-MM-DD), sonst die neueste. */
    ListenDatum?: string | null;
    /** Die zuletzt abgelegten Dokumente dieser Art am Mandanten, neueste zuerst. */
    VorhandenArray: {
      idDocument: number;
      FileName?: string | null;
      Created_at?: string | null;
    }[];
  };
}

/** Body von `POST /ki/registerabruf` — Angaben aus der Registersuche. */
export interface IKiRegisterabrufOhneMandant {
  art: 'AD' | 'GL';
  Firma: string;
  /** Vollständig, z. B. „HRB 12345". */
  RegisterNumber: string;
  idCourt: number;
  /** Nur GL: eine bestimmte Liste (YYYY-MM-DD) statt der neuesten. */
  Datum?: string;
}

/** Eine weitere Gesellschafterliste im Register. Ohne `Datum` (Aufnahme-
 *  datum unbekannt) lässt sie sich nicht gezielt abrufen. */
export interface IKiWeitereListe {
  Datum: string | null;
  Bezeichnung: string;
}

/** Antwort von `POST /ki/registerabruf`: die Datei — oder der Grund. */
export interface IKiRegisterabrufDatei {
  FileName?: string;
  MimeType?: string;
  /** Base64. */
  Data?: string;
  Fehler?: string;
  /** Nur GL: welche Liste es ist und was das Register sonst noch führt. */
  Datum?: string | null;
  WeitereListen?: IKiWeitereListe[];
}

/** Antwort des Registerabrufs: je Typ die id des abgelegten Dokuments. */
export interface IRegisterDocumentsResult {
  AD?: number | null;
  SI?: number | null;
  GL?: number | null;
  /** Nur gesetzt, wenn ein Typ leer blieb — mit dem Grund. */
  Fehler?: Partial<Record<'AD' | 'SI' | 'GL', string>>;
  /** Nur wenn eine Gesellschafterliste kam. */
  GLDatum?: string | null;
  WeitereGL?: IKiWeitereListe[];
}

/**
 * Wohin eine Datei aus dem Chat abgelegt wird — der Body von
 * `POST /ki/ablage/{id}` (ohne die Datei, die hängt das Chatfenster an).
 */
export type IKiAblagePayload = {
  idLegalTransaction: number;
} & (
  | { ziel: 'massnahme'; idLegalRequirement: number }
  | { ziel: 'urkunden' }
  | {
      ziel: 'datenablage';
      /** `null` heißt: neuer Ordner mit dem Namen aus `neuerOrdner`. */
      idDocumentDirectory: number | null;
      neuerOrdner: string | null;
    }
);

/**
 * Eine Datei aus dem Chat, vorbereitet zur Ablage im Vorgang.
 *
 * Die Datei selbst kommt **nicht** vom Server zurück. Das Chatfenster hängt
 * sie an (`Datei`): es ist die, die mit genau dieser Frage geschickt wurde.
 * Weder sie noch die Karte werden im `sessionStorage` gehalten.
 */
export interface IKiAblageProposal extends IKiProposalBase {
  Kind: 'ablage';
  Payload: IKiAblagePayload;
  Display: {
    Datei: string | null;
    Vorgang: string | null;
    Aktenzeichen?: string | null;
    Ziel: string;
    Massnahme?: string | null;
    Phase?: string | null;
    Ordner?: string | null;
    /**
     * Was die Ablage auslöst — Sichtbarkeit im Portal, E-Mail an die
     * Mandanten. Steht vor dem Klick, nicht danach.
     */
    Folgen?: string[];
    /** Sehen oder bekommen die Mandanten dadurch etwas? */
    BetrifftMandanten?: boolean;
  };
  /** Vom Chatfenster gesetzt, nicht vom Server. */
  Datei?: IKiAttachment;
}

/**
 * Ein vorbereiteter DATEV-Export. Exportiert ist nichts: die Karte öffnet den
 * Exportdialog der Rechnungsübersicht mit diesem Zeitraum.
 */
export interface IKiDatevExportProposal extends IKiProposalBase {
  Kind: 'datevexport';
  Payload: { von: string; bis: string; mitZahlungen: boolean };
  Display: {
    AnzahlRechnungen: number;
    SummeRechnungen: number;
    AnzahlZahlungen: number;
    SummeZahlungen: number;
  };
}

/**
 * Ein neuer Vorgang, vorbereitet. Die Karte öffnet den Dialog „Neuer Vorgang"
 * damit vorbelegt — angelegt wird dort.
 */
export interface IKiVorgangProposal extends IKiProposalBase {
  Kind: 'vorgang';
  Payload: {
    idLegalTransactionType: number;
    Title: string;
    Notary_idUser: number | null;
    Employee_idUser: number | null;
    idUserGroup: number | null;
    /** `idLegalRole` = id der Rollenvorlage der Vorgangsart. */
    LegalClientArray: { idLegalClient: number; idLegalRole: number | null }[];
  };
  Display: {
    Vorgangsart: string | null;
    Notar: string | null;
    Mitarbeiter: string | null;
    Gruppe: string | null;
    Beteiligte: { Name: string; Rolle: string | null }[];
  };
}

/** Ein neuer Status für einen Vorgang — In Vorbereitung, Laufend, Inaktiv. */
export interface IKiVorgangStatusProposal extends IKiProposalBase {
  Kind: 'vorgangstatus';
  Payload: { idLegalTransaction: number; idLegalTransactionState: number };
  Display: {
    Vorgang: string | null;
    Aktenzeichen: string | null;
    Von: string | null;
    Nach: string;
  };
}

export type IKiProposal =
  | IKiVorgangProposal
  | IKiVorgangStatusProposal
  | IKiTaskProposal
  | IKiGrundbuchProposal
  | IKiMandantProposal
  | IKiAusweiseProposal
  | IKiDownloadProposal
  | IKiRegisterAbrufProposal
  | IKiAblageProposal
  | IKiDatevExportProposal
  | IKiKontrollstrukturProposal
  | IKiKontrollstrukturAblageProposal
  | IKiInternetFreigabeProposal
  | IKiInternetQuellenProposal;

/** `Payload` eines Aufgaben-Vorschlags — passt auf `POST /task/fullObject`. */
export interface IKiTaskPayload {
  idTask: number;
  Title: string;
  Body: string;
  Deadline_at: string;
  idTaskType: number;
  idTaskPriority: number;
  idLegalTransaction: number | null;
  idLegalClient: number | null;
  Users: number[];
  UserGroups: number[];
}

export interface IKiCompareResult {
  ResultArray: IKiAnswer[];
}

/** Eine Quelle aus der Internetsuche. */
export interface IKiInternetQuelle {
  url: string;
  titel: string;
}

/** Was an einer Antwort festhält, dass das Internet benutzt wurde. */
export interface IKiInternetVerwendung {
  Anfrage: string;
  /** Über welchen Suchdienst gesucht wurde („Google Gemini"). */
  Suchdienst?: string | null;
  Quellen: IKiInternetQuelle[];
  /** Die Anfrage enthielt (freigegebene) Mandantendaten. */
  MitMandantendaten: boolean;
  /** Quellen nur vom Modell genannt, nicht als rohe Treffer geliefert. */
  QuellenUngeprueft?: boolean;
}

/** Karte: jede Internetsuche erst nach Freigabe (#1336, 15.10.2026). */
export interface IKiInternetFreigabeProposal extends IKiProposalBase {
  Kind: 'internet_freigabe';
  Payload: { idFreigabe: number };
  Display: {
    Anfrage: string;
    Mandanten: string[];
    Suchdienst?: string | null;
  };
}

/** Kein Vorschlag, sondern die Kennzeichnung: hier wurde gesucht. */
export interface IKiInternetQuellenProposal extends IKiProposalBase {
  Kind: 'internet_quellen';
  Payload: Record<string, never>;
  Display: IKiInternetVerwendung;
}

/** Eine Zeile im Chatfenster. */
export interface IKiMessage {
  role: 'user' | 'assistant';
  content: string;
  /** Nur bei Antworten: die Namen der nachgeschlagenen Werkzeuge. */
  tools?: string[];
  /**
   * Nur bei Antworten: offene Vorschläge. Bleiben bewusst **nicht** im
   * `sessionStorage` — ein Vorschlag von gestern, der nach einem Neuladen
   * wieder anklickbar wäre, legt eine Aufgabe an, die niemand mehr erwartet.
   */
  proposals?: IKiProposal[];
  /**
   * Nur bei Antworten: das Internet wurde benutzt. Bleibt — anders als die
   * Vorschläge — auch nach dem Neuladen an der Antwort, denn die
   * Kennzeichnung gehört zur Antwort selbst (Vorgabe 15.10.2026).
   */
  internet?: IKiInternetVerwendung[];
}

/**
 * Ergebnis von `POST /ki/grundbuch/analyse` (#1336).
 *
 * Dieselbe Auslesung, die auch das Chat-Werkzeug benutzt — nur über einen
 * Upload statt über einen Anhang. `Draft` passt auf `initialDraft` des
 * Blatt-Editors; gespeichert wurde nichts.
 */
export interface IKiGrundbuchAnalysis {
  Draft: Record<string, unknown>;
  AmtsgerichtText: string | null;
  GemarkungText: string | null;
  EigentuemerArray: IKiGrundbuchOwner[];
  /** Gesetzt, wenn es das Blatt in DiNo schon gibt → ergänzen statt anlegen. */
  idGrundbuchblatt: number | null;
  Confidence: 'hoch' | 'mittel' | 'niedrig' | null;
  Reason: string | null;
  ProviderLabel: string;
  Model: string | null;
  DurationMs: number | null;
}

/** Die vier Arten, die der Zuordnungsdialog kennt. */
export type IKiDocumentKind =
  | 'document'
  | 'certificate'
  | 'invoice'
  | 'landregister'
  | 'unknown';

/** Ein Vorgang, den der Abgleich der Urkundennummer gefunden hat. */
export interface IKiRecordMatch {
  idVorgang: number;
  vorgang: string | null;
  urkundennummer: string | null;
  gefunden_als: string;
  treffer: string;
}

/** Das Ergebnis der Untersuchung eines Posteingangs-Dokuments. */
export interface IKiPosteingangAnalysis {
  Kind: IKiDocumentKind;
  KindLabel: string;
  /** Die Sektion des Zuordnungsdialogs, in die das Dokument gehört. */
  Section: string | null;
  idLegalTransaction: number | null;
  LegalTransactionTitle: string | null;
  idLegalRequirement: number | null;
  LegalRequirement: string | null;
  Confidence: 'hoch' | 'mittel' | 'niedrig' | null;
  Reason: string | null;
  /**
   * Dem Schreiben liegt ein vollständiger Grundbuchausdruck bei. Unabhängig
   * von `Kind`: eine Eintragungsbekanntmachung gehört an eine Maßnahme **und**
   * trägt oft den Ausdruck als Anlage — beides soll möglich sein.
   */
  LandRegisterEnclosed: boolean;
  RecordNumberArray: string[];
  MatchArray: IKiRecordMatch[];
  ProviderLabel: string;
  Model: string | null;
  DurationMs: number | null;
}
